import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopyToClipboard } from 'react-use'

import CopyIcon from '@/assets/icons/copy.svg?react'
import { Loading, message, Trans } from '@/components'
import { useNATListQuery } from '@/hooks/useNATListQuery'
import { Instance } from '@/models/instance'
import { formatBitsPerSec } from '@/utils/formatter'
import { cn } from '@/utils/style'

interface InstancePublicIPProps {
  instance: Instance
}

export function InstancePublicIP({ instance }: InstancePublicIPProps) {
  const { t } = useTranslation()
  const [state, copyToClipboard] = useCopyToClipboard()

  const nat = instance.nats?.[0]
  const netResID = nat?.net.netResID
  const publicIP = nat?.net.networkName

  const { networks, isLoading, isError } = useNATListQuery({
    params: {
      page: 1,
      per_page: 50,
      network_type: 6,
      regions: [instance.domain],
      region_filter_criteria: 0,
    },
    enabled: !!netResID,
  })

  const natInfo = useMemo(
    () => networks.find((n) => n.resID === netResID) ?? null,
    [networks, netResID],
  )

  useEffect(() => {
    if (state.value) {
      message.success(t('instance.access.message.copied'))
    }
  }, [state.value, t])

  useEffect(() => {
    if (isError) {
      message.error(t('instance.detail.publicIPFetchError'))
    }
  }, [isError, t])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loading />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-4 gap-4">
      <div>
        <div className="t-xs text-secondary mb-1">
          <Trans i18nKey="instance.access.publicIPId">ID</Trans>
        </div>
        <div className="t-sb text-primary">{netResID || '-'}</div>
      </div>
      <div>
        <div className="t-xs text-secondary mb-1">
          <Trans ns="eip" i18nKey="detail.infoStatus">
            Status
          </Trans>
        </div>
        <div className="t-sb text-active">Available</div>
      </div>
      <div>
        <div className="t-xs text-secondary mb-1">
          <Trans i18nKey="instance.access.publicIPAddress">Public IP Address</Trans>
        </div>
        <div className="t-sb text-primary flex items-center gap-x-1">
          {publicIP || '-'}
          {publicIP ? (
            <CopyIcon
              className={cn('w-4 h-4 text-btn-secondary inline-block cursor-pointer')}
              onClick={() => copyToClipboard(publicIP)}
            />
          ) : null}
        </div>
      </div>
      <div>
        <div className="t-xs text-secondary mb-1">
          <Trans ns="eip" i18nKey="detail.infoBandwidthCap">
            Bandwidth Capacity
          </Trans>
        </div>
        <div className="t-sb text-primary">
          {!natInfo?.bandwidth
            ? '-'
            : natInfo.bandwidth.bandwidthType === 0
              ? 'Shared'
              : formatBitsPerSec(natInfo.bandwidth.rateBitsPerSec)}
        </div>
      </div>
    </div>
  )
}
